import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';

// Base axios configuration
const apiConfig = {
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
};

// Create axios instance
const api = axios.create(apiConfig);

// Add auth token to headers if it exists
export const addAuthToken = (token) => {
    if (token) {
        // Ensure token has Bearer prefix
        const tokenWithBearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        api.defaults.headers.common['Authorization'] = tokenWithBearer;
    }
};

// Remove auth token from headers
export const removeAuthToken = () => {
    delete api.defaults.headers.common['Authorization'];
};

// Request interceptor
api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                // Check if token is expired
                const tokenParts = token.split('.');
                if (tokenParts.length === 3) {
                    const tokenPayload = JSON.parse(atob(tokenParts[1]));
                    if (Date.now() > tokenPayload.exp * 1000) {
                        console.log('Token expired, clearing from storage');
                        localStorage.removeItem('token');
                        sessionStorage.removeItem('token');
                        localStorage.removeItem('employeeToken');
                        removeAuthToken();
                        
                        // Dispatch unauthorized event
                        window.dispatchEvent(new CustomEvent('unauthorized', {
                            detail: { status: 401, message: 'Token expired' }
                        }));
                        
                        return Promise.reject(new Error('Token expired'));
                    }
                }
                
                // Ensure token has Bearer prefix
                const tokenWithBearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
                config.headers.Authorization = tokenWithBearer;
            } catch (error) {
                console.error('Error parsing token:', error);
                localStorage.removeItem('token');
                removeAuthToken();
            }
        }
        console.log('Making request to:', API_BASE_URL + config.url, 'with data:', config.data);
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        console.log('Response received:', response.data);
        return response;
    },
    async (error) => {
        console.error('Response error:', error);
        console.error('Error response data:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        if (error.response?.status === 401) {
            // Check if this is a TOKEN_OUTDATED error
            if (error.response?.data?.code === 'TOKEN_OUTDATED') {
                console.log('Token outdated - user permissions were updated');
                
                // Clear token and notify app about outdated token
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                localStorage.removeItem('employeeToken');
                removeAuthToken();
                
                // Dispatch custom event for token outdated
                window.dispatchEvent(new CustomEvent('unauthorized', {
                    detail: { 
                        status: 401, 
                        message: error.response.data.message || 'Your permissions have been updated. Please log in again.',
                        code: 'TOKEN_OUTDATED'
                    }
                }));
                
                return Promise.reject(error.response.data);
            }
            
            // Try to refresh the token for other 401 errors
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const refreshResponse = await axios.post(`${API_BASE_URL}/api/auth/refresh`, null, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (refreshResponse.data?.token) {
                        // Update token in localStorage and headers
                        localStorage.setItem('token', refreshResponse.data.token);
                        addAuthToken(refreshResponse.data.token);
                        
                        // Retry the original request with new token
                        const originalRequest = error.config;
                        originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
                        return axios(originalRequest);
                    }
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                // Clear token and notify app about unauthorized access
                localStorage.removeItem('token');
                removeAuthToken();
                
                // Dispatch custom event for unauthorized access
                window.dispatchEvent(new CustomEvent('unauthorized', {
                    detail: { status: 401, message: 'Unauthorized access' }
                }));
            }
        }
        
        // Throw the error with the most detailed information available
        const errorToThrow = error.response?.data || error;
        console.error('Throwing error:', errorToThrow);
        return Promise.reject(errorToThrow);
    }
);

// Login function for managers
export const login = async (email, password) => {
    try {
        console.log('Attempting manager login with:', { email });
        const response = await api.post('/api/users/login', { 
            email, 
            password
        });
        
        console.log('Login response:', response.data);
        
        if (response.data?.token) {
            // Store the token
            localStorage.setItem('token', response.data.token);
            // Set up headers for future requests
            addAuthToken(response.data.token);
            console.log('Manager login successful');
            return response.data;
        } else {
            console.error('No token in response:', response.data);
            throw new Error('No token received from login');
        }
    } catch (error) {
        console.error('Manager login failed:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
            throw error.response.data;
        }
        throw error;
    }
};

// Register function
export const register = async (email, password, company_name, first_name, last_name) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/users/register`, { 
            email, 
            password, 
            company_name,
            first_name,
            last_name,
            role: 'manager'
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Search users in the company
export const searchUsers = async (query) => {
    try {
        const response = await api.get(`/api/users/search?query=${encodeURIComponent(query)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Grant manager access to a user
export const grantManagerAccess = async (userId) => {
    try {
        const response = await api.put(`/api/users/${userId}/grant-manager-access`);
        
        // If the response includes a new token, update the current user's token if they're the one being promoted
        if (response.data.token) {
            const currentToken = localStorage.getItem('token');
            if (currentToken) {
                try {
                    const currentTokenPayload = JSON.parse(atob(currentToken.split('.')[1]));
                    // If the promoted user is the current user, update their token
                    if (currentTokenPayload.id === parseInt(userId)) {
                        localStorage.setItem('token', response.data.token);
                        addAuthToken(response.data.token);
                        console.log('Updated token for promoted user');
                    }
                } catch (error) {
                    console.error('Error parsing current token:', error);
                }
            }
        }
        
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Set role manager status for a user
export const setRoleManagerStatus = async (userId, isRoleManager, roleToManage) => {
    try {
        const response = await api.put(`/api/users/${userId}/set-role-manager`, {
            isRoleManager,
            roleToManage
        });
        
        // If the response includes a new token, update the current user's token if they're the one being promoted
        if (response.data.token) {
            const currentToken = localStorage.getItem('token');
            if (currentToken) {
                try {
                    const currentTokenPayload = JSON.parse(atob(currentToken.split('.')[1]));
                    // If the promoted user is the current user, update their token
                    if (currentTokenPayload.id === parseInt(userId)) {
                        localStorage.setItem('token', response.data.token);
                        addAuthToken(response.data.token);
                        console.log('Updated token for promoted role manager');
                    }
                } catch (error) {
                    console.error('Error parsing current token:', error);
                }
            }
        }
        
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get user profile with manager status
export const getUserProfile = async () => {
    try {
        const response = await api.get('/api/users/profile');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Get all employees in company with manager status
export const getEmployees = async () => {
    try {
        const response = await api.get('/api/users/employees');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export default api;
